import React, { useCallback, useEffect, useRef, useState } from 'react';

import { GoogleMap, Marker, Polygon, Polyline, Rectangle, useLoadScript } from '@react-google-maps/api';

import { Box, Card, CardContent, CircularProgress, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { Cluster, ClusterOverlay, LatLongTuple } from '~/components/LERE/types';

import ClusterPolygonOverlay from './ClusterPolygonOverlay';

const MAP_CONTAINER_STYLE: React.CSSProperties = {
  width: '100%',
  height: '700px',
};

const MAX_LATITUDE = 85.0511;

// Restrict map panning to world bounds
const MAP_RESTRICTION = {
  latLngBounds: {
    north: MAX_LATITUDE,
    south: -MAX_LATITUDE,
    east: 180,
    west: -180,
  },
  strictBounds: true,
};

// Convert [lat, lng] tuple to Google Maps LatLng object
function toLatLng(point: LatLongTuple): google.maps.LatLngLiteral {
  return { lat: point[0], lng: point[1] };
}

// Convert array of [lat, lng] tuples to Google Maps LatLng objects
function toLatLngArray(points: Array<LatLongTuple>): Array<google.maps.LatLngLiteral> {
  return points.map(toLatLng);
}

interface ClusterMapCanvasProps {
  center: LatLongTuple;
  zoom: number;
  overlays: Array<ClusterOverlay>;
  fillOpacity: number;
  selectedClusterId: number | null;
  selectedCluster?: Cluster;
  selectedClusterColor?: string;
  onSelectCluster: (clusterId: number | null) => void;
  isPickingManualBounds: boolean;
  manualPickPoints: Array<LatLongTuple>;
  manualPickPolyline: Array<LatLongTuple> | null;
  manualPickPolygon: Array<LatLongTuple> | null;
  onManualPickPointsChange: (points: Array<LatLongTuple>) => void;
  onManualPickComplete: (points: Array<LatLongTuple>) => void;
}

export default function ClusterMapCanvas({
  center,
  zoom,
  overlays,
  fillOpacity,
  selectedClusterId,
  selectedCluster,
  selectedClusterColor,
  onSelectCluster,
  isPickingManualBounds,
  manualPickPoints,
  manualPickPolyline,
  manualPickPolygon,
  onManualPickPointsChange,
  onManualPickComplete,
}: ClusterMapCanvasProps) {
  const theme = useTheme();
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: window.configs.GOOGLE_PLACE_API_KEY,
  });

  const mapRef = useRef<google.maps.Map | null>(null);
  const lastViewRef = useRef<{ center: LatLongTuple; zoom: number } | null>(null);
  const [mapOptions, setMapOptions] = useState<google.maps.MapOptions>({});

  const pickColor = theme.palette.primary.main;
  const showClusters = !isPickingManualBounds;
  const showBounds = isPickingManualBounds;
  const pickMarkersVisible = isPickingManualBounds && manualPickPoints.length > 0;

  // Update map options based on picking mode
  useEffect(() => {
    setMapOptions({
      restriction: MAP_RESTRICTION,
      disableDefaultUI: false,
      zoomControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      draggable: !isPickingManualBounds,
      draggableCursor: isPickingManualBounds ? 'crosshair' : undefined,
    });
  }, [isPickingManualBounds]);

  // Handle map load
  const onLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  // Update map view when center or zoom changes
  useEffect(() => {
    if (!mapRef.current) {
      return;
    }

    const lastView = lastViewRef.current;
    const hasChanged =
      !lastView || lastView.zoom !== zoom || lastView.center[0] !== center[0] || lastView.center[1] !== center[1];

    if (hasChanged) {
      mapRef.current.setCenter(toLatLng(center));
      mapRef.current.setZoom(zoom);
      lastViewRef.current = { center, zoom };
    }
  }, [center, zoom]);

  // Handle map click for manual bounds picking
  const handleMapClick = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (!isPickingManualBounds || !event.latLng) {
        return;
      }

      const lat = Math.min(Math.max(event.latLng.lat(), -MAX_LATITUDE), MAX_LATITUDE);
      const lng = Math.min(Math.max(event.latLng.lng(), -180), 180);
      const nextPoint: LatLongTuple = [lat, lng];

      if (manualPickPoints.length >= 4) {
        return;
      }

      const nextPoints = [...manualPickPoints, nextPoint];
      onManualPickPointsChange(nextPoints);

      if (nextPoints.length === 4) {
        onManualPickComplete(nextPoints);
      }
    },
    [isPickingManualBounds, manualPickPoints, onManualPickPointsChange, onManualPickComplete],
  );

  // Handle marker drag for manual bounds
  const handleMarkerDragEnd = useCallback(
    (index: number, event: google.maps.MapMouseEvent) => {
      if (!event.latLng) {
        return;
      }

      const lat = Math.min(Math.max(event.latLng.lat(), -MAX_LATITUDE), MAX_LATITUDE);
      const lng = Math.min(Math.max(event.latLng.lng(), -180), 180);
      const nextPoints = [...manualPickPoints];
      nextPoints[index] = [lat, lng];
      onManualPickPointsChange(nextPoints);
    },
    [manualPickPoints, onManualPickPointsChange],
  );

  if (loadError) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error">Error loading Google Maps</Typography>
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading map...</Typography>
      </Box>
    );
  }

  const boundsRectangle = {
    north: MAX_LATITUDE,
    south: -MAX_LATITUDE,
    east: 180,
    west: -180,
  };

  return (
    <Box sx={{ position: 'relative', borderRadius: 1, overflow: 'hidden' }}>
      <GoogleMap
        mapContainerStyle={MAP_CONTAINER_STYLE}
        center={toLatLng(center)}
        zoom={zoom}
        options={mapOptions}
        onLoad={onLoad}
        onClick={handleMapClick}
      >
        {/* Show bounds rectangle when picking manual bounds */}
        {showBounds && (
          <Rectangle
            bounds={boundsRectangle}
            options={{
              strokeColor: theme.palette.warning.main,
              strokeWeight: 1,
              fillOpacity: 0.03,
              strokeOpacity: 1,
            }}
          />
        )}

        {/* Manual pick polyline (2-3 points) */}
        {manualPickPolyline && (
          <Polyline
            path={toLatLngArray(manualPickPolyline)}
            options={{
              strokeColor: pickColor,
              strokeWeight: 2,
            }}
          />
        )}

        {/* Manual pick polygon (4 points, closed) */}
        {manualPickPolygon && (
          <Polygon
            paths={toLatLngArray(manualPickPolygon)}
            options={{
              strokeColor: pickColor,
              strokeWeight: 2,
              fillOpacity: 0.05,
              strokeOpacity: 1,
            }}
          />
        )}

        {/* Draggable markers for manual pick points */}
        {pickMarkersVisible &&
          manualPickPoints.map((point, index) => (
            <Marker
              key={`manual-pick-${index}`}
              position={toLatLng(point)}
              draggable
              icon={{
                path: google.maps.SymbolPath.CIRCLE,
                scale: 6,
                fillColor: theme.palette.common.white,
                fillOpacity: 1,
                strokeColor: pickColor,
                strokeWeight: 2,
              }}
              onDragEnd={(event) => handleMarkerDragEnd(index, event)}
            />
          ))}

        {/* Cluster polygon overlays */}
        {showClusters &&
          overlays.map((overlay) => (
            <ClusterPolygonOverlay
              key={overlay.cluster.cluster_id}
              overlay={overlay}
              isSelected={overlay.cluster.cluster_id === selectedClusterId}
              fillOpacity={fillOpacity}
              onSelect={onSelectCluster}
            />
          ))}
      </GoogleMap>

      {/* Selected cluster info card */}
      {showClusters && selectedCluster && (
        <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
          <Card variant="outlined" sx={{ minWidth: 180 }}>
            <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: selectedClusterColor ?? '#000',
                    flexShrink: 0,
                  }}
                />
                <Typography variant="subtitle2">Cluster {selectedCluster.cluster_id}</Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {selectedCluster.offer_count} offers
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
}
